(async () => {
  // Floating-ui (popper.js successor library): https://floating-ui.com/docs/tutorial
  const { computePosition, autoUpdate, flip, shift, offset } = await import('@floating-ui/dom');

  class FloatUI extends HTMLElement {
    constructor() {
      super();

      this.defaults = {
        onHover: false,
        placement: 'left',
        triggerOffset: -20,
        shiftOffset: 8,
        closeOthers: true,
        isContentFocusable: true,
        hideOnWindowScroll: true,
        positionOnMobile: false,
      };

      this.options = {
        ...this.defaults,
        ...(this.dataset.options && JSON.parse(this.dataset.options)),
      };

      this.trigger = this.querySelector('[data-float-ui-trigger]');
      this.target = this.querySelector('[data-float-ui-target]');
      this.isShowing = false;
      // the x Position will be used to update the position of the tooltip if there's a change between Desktop & Mobile
      this.xPosition;

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        focus: (e) => this.show(e),
        blur: (e) => this.hide(e),
        mousedown: (e) => this.toggle(e),
        mouseEnter: (e) => this.show(e),
        mouseLeave: (e) => this.hide(e),
        clickOutside: (e) => this.onBodyClick(e),
        scroll: (e) => this.onWindowScroll(e),
        resize: (e) => this.onWindowResize(e),
      };

      this.addEvents();
    }

    /**
     * Add the necessary events
     */
    addEvents() {
      if (this.options.onHover) {
        this.trigger.addEventListener('mouseenter', this.reducer.mouseEnter);
        this.addEventListener('mouseleave', this.reducer.mouseLeave);
      } else {
        this.trigger.addEventListener('click', this.reducer.mousedown);
      }
    }

    /**
     * Toggles the showing/hiding
     */
    toggle(e) {
      if (this.isShowing) {
        this.hide();
      } else {
        this.show(e);
      }
    }

    /**
     * Shows the target
     */
    show(e) {
      e?.preventDefault();

      // Set states
      this.classList.add('float--active');
      this.target.ariaHidden = false;
      this.target.classList.remove('hidden');
      this.trigger.setAttribute('aria-expanded', true);
      this.isShowing = true;

      // Set boundary to keep the popup within section/image, so it doesn’t overflow
      const boundary = this.closest('section') || document.body;

      // Set position
      this.updateAndCleanup = autoUpdate(this.trigger, this.target, () => {
        computePosition(this.trigger, this.target, {
          strategy: 'fixed',
          placement: this.options.placement,
          middleware: [
            offset(this.options.triggerOffset),
            shift({
              padding: this.options.shiftOffset,
              boundary,
            }),
            flip({
              padding: this.options.shiftOffset,
              boundary,
            }),
          ],
        }).then(({ x, y }) => {
          this.xPosition = x;
          const isDesktop = window.innerWidth >= 768;
          const applyPosition = isDesktop || this.options.positionOnMobile;
          if (applyPosition) {
            Object.assign(this.target.style, {
              left: `${x}px`,
              top: `${y}px`,
              maxWidth: isDesktop
                ? `calc(100vw - (${x}px + 32px))`
                : `calc(100vw - ${this.options.shiftOffset * 2}px)`,
            });
          }
        });
      });

      // Add click outside event to body
      document.body.addEventListener('click', this.reducer.clickOutside);

      // Add on window scroll event
      if (this.options.hideOnWindowScroll) window.addEventListener('scroll', this.reducer.scroll);

      // Add on window resize event
      window.addEventListener('resize', this.reducer.resize);

      // Close other floats
      if (this.options.closeOthers) {
        document.querySelectorAll('float-ui')?.forEach((float) => {
          if (float !== this && float.isShowing) float.hide();
        });
      }

      // Focus on inner elements if needed
      if (this.options.isContentFocusable && e.type === 'focus') this.trapFocus();
    }

    /**
     * Hides the target
     */
    hide() {
      this.classList.remove('float--active');
      this.target.ariaHidden = true;
      this.target.classList.add('hidden');
      this.trigger.setAttribute('aria-expanded', false);
      this.trigger.blur();
      this.isShowing = false;

      this.updateAndCleanup();
      document.body.removeEventListener('click', this.reducer.clickOutside);

      if (this.options.isContentFocusable) removeTrapFocus(this);
    }

    /**
     * Onclick outside
     * @param {Event} event
     */
    onBodyClick({ target }) {
      const isCloseElement =
        target == this.querySelector('[data-float-ui-close]') ||
        target.closest('[data-float-ui-close]') ||
        target == this.querySelector('.backdrop');

      // close when clicking anywhere except on the float itself, with the exception of closing elements (backdrop & close button on mobile)
      if ((target !== this && !target.closest('float-ui') && this.isShowing) || isCloseElement) {
        this.hide();
      }
    }

    /**
     * On window scroll
     */
    onWindowScroll() {
      this.hide();
      window.removeEventListener('scroll', this.reducer.scroll);
    }

    /**
     * On window resize from Desktop to Mobile, reset the positioning and width values
     */
    onWindowResize() {
      if (this.xPosition != 0 && window.innerWidth < 768 && !this.options.positionOnMobile) {
        this.xPosition = 0;
        Object.assign(this.target.style, {
          left: this.xPosition,
          top: 'auto',
          maxWidth: 'none',
        });
        window.removeEventListener('resize', this.reducer.resize);
      }
    }

    /**
     * Trap the focus inside
     */
    trapFocus() {
      const focusableChildren = this.target.querySelectorAll(
        'a, label, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      );
      const focusableElements = [...focusableChildren].filter(
        (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
      );

      if (focusableElements?.length > 0) trapFocus(this.target, focusableElements[0]);
    }
  }

  window.FloatUI = FloatUI;
  customElements.define('float-ui', FloatUI);
})();
